import { useEffect, useMemo, useRef, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import {
  ArrowLeftIcon,
  ArrowsRightLeftIcon,
  AcademicCapIcon,
  ArchiveBoxIcon,
  Bars3Icon,
  ChatBubbleLeftEllipsisIcon,
  CheckIcon,
  ChevronDownIcon,
  DocumentTextIcon,
  LockClosedIcon,
  NoSymbolIcon,
  PencilSquareIcon,
  SparklesIcon,
  VideoCameraIcon,
  XMarkIcon,
  StarIcon as StarOutline,
} from '@heroicons/react/24/outline'
import { compareResumeToApplication, hasCurrentResume } from '../services/resumeService'
import { StarIcon as StarSolid } from '@heroicons/react/24/solid'
import { ProBadge } from './UpgradePrompt'
import type { Application, ApplicationStatus, Currency, RejectReason, RejectReasonCode, SalaryPeriod, SalaryRange, SubscriptionPlan, UserSettings, WorkModeType } from '../types'
import {
  rejectOptions,
  statusBadgeClass,
} from '../utils'
import { getValidTransitions, STATUS_LABELS, isTerminalStatus } from '../utils/statusMachine'
import {
  convertSalaryRange,
  convertCurrency,
  convertToDisplayFormat,
  convertPeriod,
  formatCurrencyAmount,
  getPeriodSuffix,
  getFormatSuffix,
  SUPPORTED_CURRENCIES,
} from '../utils/currency'
import { formatDate } from '../utils/dateFormat'
import { PRESET_STAGE_OPTIONS } from '../services/stagePresets'

const createId = () =>
  typeof crypto !== 'undefined' && crypto.randomUUID
    ? crypto.randomUUID()
    : Math.random().toString(36).slice(2)

type Props = {
  application: Application
  onToggleStage: (appId: string, stageId: string) => void
  onUpdateStages: (appId: string, stages: Application['processStages']) => void
  onUpdateCompensation: (
    appId: string,
    payload: {
      salaryRange: SalaryRange
      targetAmount: number | null
      targetPeriod: SalaryPeriod
      targetCurrency: Currency
    }
  ) => Promise<void> | void
  onUpdateStatus: (appId: string, status: ApplicationStatus, offerReason?: string | null) => void
  onUpdateNote: (appId: string, note: string) => void
  onUpdateDetails: (appId: string, details: { positionTitle?: string | null; contractType?: string | null; benefits?: string | null; techStack?: string[]; workMode?: WorkModeType; officeDays?: number | null }) => void
  onArchive: (appId: string) => void
  onUnarchive: (appId: string) => void
  onSubmitReject: (appId: string, reason: RejectReason) => void
  onToggleFavorite: (appId: string) => void
  settings: UserSettings
  plan: SubscriptionPlan
}

export const ApplicationDetailPage = ({
  application,
  onToggleStage,
  onUpdateStages,
  onUpdateCompensation,
  onUpdateStatus,
  onUpdateNote,
  onUpdateDetails,
  onArchive,
  onUnarchive,
  onSubmitReject,
  onToggleFavorite,
  settings,
  plan,
}: Props) => {
  const app = application
  const isArchived = app.archived

  // Note editing
  const [isEditingNote, setIsEditingNote] = useState(false)
  const [noteDraft, setNoteDraft] = useState(app.note ?? '')
  const noteTextareaRef = useRef<HTMLTextAreaElement | null>(null)

  // Stages editing
  const [isEditingStages, setIsEditingStages] = useState(false)
  const [stageDrafts, setStageDrafts] = useState(app.processStages)
  const [isSavingStages, setIsSavingStages] = useState(false)
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null)

  // Compensation editing
  const [isEditingComp, setIsEditingComp] = useState(false)
  const [isSavingComp, setIsSavingComp] = useState(false)
  const [salaryMinDraft, setSalaryMinDraft] = useState<string | number>('')
  const [salaryMaxDraft, setSalaryMaxDraft] = useState<string | number>('')
  const [salaryCurrencyDraft, setSalaryCurrencyDraft] = useState<Currency>('PLN')
  const [salaryPeriodDraft, setSalaryPeriodDraft] = useState<SalaryPeriod>('monthly')
  const [targetAmountDraft, setTargetAmountDraft] = useState<string | number>('')
  const [targetPeriodDraft, setTargetPeriodDraft] = useState<SalaryPeriod>('monthly')
  const [targetCurrencyDraft, setTargetCurrencyDraft] = useState<Currency>('PLN')
  const [compError, setCompError] = useState<string | null>(null)

  // Position title editing
  const [isEditingPosition, setIsEditingPosition] = useState(false)
  const [positionTitleDraft, setPositionTitleDraft] = useState(app.positionTitle ?? '')

  // Contract details editing
  const [isEditingContract, setIsEditingContract] = useState(false)
  const [contractTypeDraft, setContractTypeDraft] = useState(app.contractType ?? '')
  const [benefitsDraft, setBenefitsDraft] = useState(app.benefits ?? '')

  // Tech stack editing
  const [isEditingTechStack, setIsEditingTechStack] = useState(false)
  const [techStackDraft, setTechStackDraft] = useState(app.techStack.join(', '))

  // Work mode editing
  const [isEditingWorkMode, setIsEditingWorkMode] = useState(false)
  const [workModeDraft, setWorkModeDraft] = useState<WorkModeType>(app.workMode ?? null)
  const [officeDaysDraft, setOfficeDaysDraft] = useState<number | null>(app.officeDays ?? null)

  // Status modal
  const [isStatusModalOpen, setIsStatusModalOpen] = useState(false)
  const [statusDraft, setStatusDraft] = useState<ApplicationStatus>(app.status)
  const [offerReasonDraft, setOfferReasonDraft] = useState(app.offerDecisionReason ?? '')
  const statusModalRef = useRef<HTMLDivElement | null>(null)

  // Reject modal
  const [isRejectModalOpen, setIsRejectModalOpen] = useState(false)
  const [rejectState, setRejectState] = useState<RejectReason>({ code: 'too_expensive' })

  // Archive modal
  const [isArchiveModalOpen, setIsArchiveModalOpen] = useState(false)

  // Resume comparison
  const navigate = useNavigate()
  const [isResumeComparing, setIsResumeComparing] = useState(false)
  const [isNoResumeModalOpen, setIsNoResumeModalOpen] = useState(false)

  // Job posting content (expandable)
  const [isJobContentExpanded, setIsJobContentExpanded] = useState(false)

  // Pro features
  const isPro = plan === 'pro'

  // Extract domain from URL
  const getDomain = (url: string) => {
    try {
      const urlObj = new URL(url)
      return urlObj.hostname.replace(/^www\./, '')
    } catch {
      return url
    }
  }

  // Scroll to top on mount
  useEffect(() => {
    window.scrollTo(0, 0)
  }, [])

  // Sync drafts with app data
  useEffect(() => {
    setNoteDraft(app.note ?? '')
  }, [app.id, app.note])

  useEffect(() => {
    setStageDrafts(app.processStages)
  }, [app.id, app.processStages])

  useEffect(() => {
    setSalaryMinDraft(app.salaryRange?.min ?? '')
    setSalaryMaxDraft(app.salaryRange?.max ?? '')
    setSalaryCurrencyDraft((app.salaryRange?.currency as Currency) ?? 'PLN')
    setSalaryPeriodDraft(app.salaryRange?.period ?? 'monthly')
    setTargetAmountDraft(app.targetAmount ?? '')
    setTargetPeriodDraft(app.targetPeriod ?? 'monthly')
    setTargetCurrencyDraft(app.targetCurrency ?? 'PLN')
    setCompError(null)
  }, [app.id, app.salaryRange, app.targetAmount, app.targetPeriod, app.targetCurrency])

  useEffect(() => {
    setPositionTitleDraft(app.positionTitle ?? '')
  }, [app.id, app.positionTitle])

  useEffect(() => {
    setContractTypeDraft(app.contractType ?? '')
    setBenefitsDraft(app.benefits ?? '')
  }, [app.id, app.contractType, app.benefits])

  useEffect(() => {
    setTechStackDraft(app.techStack.join(', '))
  }, [app.id, app.techStack])

  useEffect(() => {
    setWorkModeDraft(app.workMode ?? null)
    setOfficeDaysDraft(app.officeDays ?? null)
  }, [app.id, app.workMode, app.officeDays])

  useEffect(() => {
    setStatusDraft(app.status)
    setOfferReasonDraft(app.offerDecisionReason ?? '')
  }, [app.id, app.status, app.offerDecisionReason])

  // Close status modal on Esc or click outside
  useEffect(() => {
    if (!isStatusModalOpen) return
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setIsStatusModalOpen(false)
    }
    const onClickOutside = (e: MouseEvent) => {
      const target = e.target as Node | null
      if (target && statusModalRef.current && !statusModalRef.current.contains(target)) {
        setIsStatusModalOpen(false)
      }
    }
    document.addEventListener('keydown', onKeyDown)
    document.addEventListener('mousedown', onClickOutside)
    return () => {
      document.removeEventListener('keydown', onKeyDown)
      document.removeEventListener('mousedown', onClickOutside)
    }
  }, [isStatusModalOpen])

  // Salary displays with currency conversion
  const displayRange = useMemo(() => {
    if (!app.salaryRange) return null
    return settings.currencyConversionEnabled
      ? convertSalaryRange(app.salaryRange, settings.targetCurrency)
      : app.salaryRange
  }, [app.salaryRange, settings.currencyConversionEnabled, settings.targetCurrency])

  // Display salary range with period
  const salaryRangeDisplay = useMemo(() => {
    if (!displayRange) return '—'
    const { min, max, currency, period } = displayRange
    const curr = (currency as Currency) || 'PLN'
    const minFormatted = formatCurrencyAmount(min, curr)
    const maxFormatted = formatCurrencyAmount(max, curr)
    const suffix = getPeriodSuffix(period)
    return `${minFormatted} – ${maxFormatted}${suffix}`
  }, [displayRange])

  // Display target salary with period
  const targetDisplay = useMemo(() => {
    if (app.targetAmount == null) return '—'
    
    // Get original currency from app, default to PLN if 'original' or missing
    const appCurrency = (app.targetCurrency && app.targetCurrency !== 'original') ? app.targetCurrency : 'PLN'
    const appPeriod = app.targetPeriod || 'monthly'
    
    let displayAmount = app.targetAmount
    let displayCurrency = appCurrency
    
    // Apply currency conversion if enabled and targetCurrency is not 'original'
    if (settings.currencyConversionEnabled && settings.targetCurrency !== 'original') {
      // If currencies are the same, no conversion needed
      if (appCurrency === settings.targetCurrency) {
        displayCurrency = settings.targetCurrency
      } else {
        // Convert to target currency
        const converted = convertCurrency(app.targetAmount, appCurrency, settings.targetCurrency as Exclude<Currency, 'original'>)
        if (converted != null) {
          displayAmount = converted
          displayCurrency = settings.targetCurrency
        }
      }
    }
    // If targetCurrency is 'original', keep original currency (already set above)
    
    // Convert period if format changed
    const suffix = settings.salaryDisplayFormat === 'original'
      ? getPeriodSuffix(appPeriod)
      : getFormatSuffix(settings.salaryDisplayFormat)
    
    let finalAmount = displayAmount
    if (settings.salaryDisplayFormat !== 'original') {
      // First convert to monthly base, then to target format
      const monthlyAmount = convertPeriod(displayAmount, appPeriod, 'monthly')
      finalAmount = convertToDisplayFormat(monthlyAmount, settings.salaryDisplayFormat) || displayAmount
    }
    
    const formatted = formatCurrencyAmount(finalAmount, displayCurrency)
    return `${formatted}${suffix}`
  }, [app.targetAmount, app.targetPeriod, app.targetCurrency, settings.salaryDisplayFormat, settings.targetCurrency, settings.currencyConversionEnabled])

  // Stage management
  const addStageDraft = () => {
    setStageDrafts((prev) => [
      ...prev,
      { id: createId(), name: PRESET_STAGE_OPTIONS[0], completed: false },
    ])
  }

  const removeStageDraft = (id: string) => {
    setStageDrafts((prev) => prev.filter((s) => s.id !== id))
  }

  const updateStageDraft = (id: string, updates: Partial<Application['processStages'][number]>) => {
    setStageDrafts((prev) => prev.map((s) => (s.id === id ? { ...s, ...updates } : s)))
  }

  const reorderStageDrafts = (from: number, to: number) => {
    if (from === to) return
    setStageDrafts((prev) => {
      const next = [...prev]
      const [item] = next.splice(from, 1)
      next.splice(to, 0, item)
      return next
    })
  }

  const handleDragStart = (index: number, event: React.DragEvent<HTMLDivElement>) => {
    if (isSavingStages) return
    setDraggedIndex(index)
    event.dataTransfer.effectAllowed = 'move'
  }

  const handleDragOver = (_index: number, event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault()
    event.dataTransfer.dropEffect = 'move'
  }

  const handleDrop = (index: number, event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault()
    if (draggedIndex !== null) {
      reorderStageDrafts(draggedIndex, index)
    }
    setDraggedIndex(null)
  }

  const handleDragEnd = () => setDraggedIndex(null)

  const saveStages = async () => {
    if (isArchived) return
    const cleaned = stageDrafts
      .map((s) => ({ ...s, name: s.name.trim() }))
      .filter((s) => s.name.length > 0)

    setIsSavingStages(true)
    try {
      await Promise.resolve(onUpdateStages(app.id, cleaned))
      setIsEditingStages(false)
    } finally {
      setIsSavingStages(false)
    }
  }

  // Compensation
  const parsePositiveNumber = (value: string | number) => {
    if (value === '' || value === null || value === undefined) return null
    const num = Number(value)
    if (!Number.isFinite(num) || num < 0) return null
    return num
  }

  const handleSaveCompensation = async () => {
    if (isArchived) return
    const salaryMin = parsePositiveNumber(salaryMinDraft)
    const salaryMax = parsePositiveNumber(salaryMaxDraft)
    const targetAmount = parsePositiveNumber(targetAmountDraft)

    if ((salaryMin === null) !== (salaryMax === null)) {
      setCompError('Enter both minimum and maximum, or leave both empty.')
      return
    }
    if (salaryMin !== null && salaryMax !== null && salaryMin > salaryMax) {
      setCompError('Minimum cannot be greater than maximum.')
      return
    }

    const salaryRange: SalaryRange =
      salaryMin != null && salaryMax != null
        ? {
            min: salaryMin,
            max: salaryMax,
            currency: salaryCurrencyDraft,
            period: salaryPeriodDraft,
          }
        : null

    setIsSavingComp(true)
    setCompError(null)
    try {
      await Promise.resolve(
        onUpdateCompensation(app.id, {
          salaryRange,
          targetAmount,
          targetPeriod: targetPeriodDraft,
          targetCurrency: targetCurrencyDraft,
        })
      )
      setIsEditingComp(false)
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to save compensation.'
      setCompError(message)
    } finally {
      setIsSavingComp(false)
    }
  }

  const handleSavePositionTitle = () => {
    if (isArchived) return
    onUpdateDetails(app.id, {
      positionTitle: positionTitleDraft || null,
    })
    setIsEditingPosition(false)
  }

  const handleSaveContractDetails = () => {
    if (isArchived) return
    onUpdateDetails(app.id, {
      contractType: contractTypeDraft || null,
      benefits: benefitsDraft || null,
    })
    setIsEditingContract(false)
  }

  const handleSaveTechStack = () => {
    if (isArchived) return
    const techArray = techStackDraft
      .split(',')
      .map((t) => t.trim())
      .filter((t) => t.length > 0)
    onUpdateDetails(app.id, { techStack: techArray })
    setIsEditingTechStack(false)
  }

  const handleSaveWorkMode = () => {
    if (isArchived) return
    onUpdateDetails(app.id, {
      workMode: workModeDraft,
      officeDays: workModeDraft === 'hybrid' ? officeDaysDraft : null,
    })
    setIsEditingWorkMode(false)
  }

  return (
    <>
    <div className="mx-auto max-w-6xl space-y-6">
      <Link
        to="/"
        className="flex items-center gap-1.5 text-sm text-app-muted hover:text-app-ink transition-colors"
      >
        <ArrowLeftIcon className="h-4 w-4" />
        Back to Dashboard
      </Link>
      <div className="card p-6 space-y-6">
          {/* Title Section */}
          <div className="border-b border-app-line pb-6 space-y-2">
            {/* Row 1: Company Name + Action Buttons */}
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={() => onToggleFavorite(app.id)}
                  className={`p-1 rounded-lg transition-colors ${
                    app.favorite
                      ? 'text-amber-700 dark:text-amber-400 hover:text-amber-700 dark:hover:text-amber-300 bg-amber-400/10'
                      : 'text-app-muted hover:text-amber-700 hover:bg-app-subtle dark:bg-app-subtle'
                  }`}
                  title={app.favorite ? 'Remove from favorites' : 'Add to favorites'}
                >
                  {app.favorite ? (
                    <StarSolid className="h-6 w-6" />
                  ) : (
                    <StarOutline className="h-6 w-6" />
                  )}
                </button>
                <h1 className="text-3xl font-bold text-app-ink font-display">{app.company}</h1>
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                <Link
                  to={`/interview?app=${app.id}`}
                  className="relative flex items-center gap-1.5 rounded-lg border border-app-line bg-app-highlight px-3 py-2 text-sm font-medium text-app-accent hover:bg-app-highlight transition-colors"
                >
                  <VideoCameraIcon className="h-4 w-4" />
                  Interview
                  {!isPro && (
                    <span className="absolute -top-2 -right-2" onClick={(e) => { e.preventDefault(); e.stopPropagation(); navigate('/pricing') }}>
                      <ProBadge source="app_detail" feature="mock_interview" iconOnly />
                    </span>
                  )}
                </Link>
                <Link
                  to={`/quiz?app=${app.id}`}
                  className="relative flex items-center gap-1.5 rounded-lg border border-app-line bg-app-highlight px-3 py-2 text-sm font-medium text-app-accent hover:bg-app-highlight transition-colors"
                >
                  <AcademicCapIcon className="h-4 w-4" />
                  Quiz
                  {!isPro && (
                    <span className="absolute -top-2 -right-2" onClick={(e) => { e.preventDefault(); e.stopPropagation(); navigate('/pricing') }}>
                      <ProBadge source="app_detail" feature="interview_quiz" iconOnly />
                    </span>
                  )}
                </Link>
                <Link
                  to={`/chat?app=${app.id}`}
                  className="relative flex items-center gap-1.5 rounded-lg border border-app-line bg-app-highlight px-3 py-2 text-sm font-medium text-app-accent hover:bg-app-highlight transition-colors"
                >
                  <ChatBubbleLeftEllipsisIcon className="h-4 w-4" />
                  Chat
                  {!isPro && (
                    <span className="absolute -top-2 -right-2" onClick={(e) => { e.preventDefault(); e.stopPropagation(); navigate('/pricing') }}>
                      <ProBadge source="app_detail" feature="ant_agent" iconOnly />
                    </span>
                  )}
                </Link>
                <button
                  onClick={async () => {
                    if (!isPro) {
                      navigate('/resume')
                      return
                    }
                    if (isResumeComparing) return
                    setIsResumeComparing(true)
                    try {
                      // Check if user has a resume before comparing
                      const hasResume = await hasCurrentResume()
                      if (!hasResume) {
                        setIsNoResumeModalOpen(true)
                        return
                      }
                      const result = await compareResumeToApplication(app.id)
                      if (result.id) {
                        navigate(`/resume/comparison/${result.id}`)
                      }
                    } catch (err) {
                      console.error('Resume comparison failed:', err)
                    } finally {
                      setIsResumeComparing(false)
                    }
                  }}
                  disabled={isResumeComparing}
                  className="relative flex items-center gap-1.5 rounded-lg border px-3 py-2 text-sm font-medium transition-colors disabled:opacity-50 border-app-line bg-app-highlight text-app-accent hover:bg-app-highlight"
                >
                  {isResumeComparing ? (
                    <span className="h-4 w-4 animate-spin rounded-full border-2 border-sky-500 border-t-transparent" />
                  ) : (
                    <DocumentTextIcon className="h-4 w-4" />
                  )}
                  Resume Match
                  {!isPro && (
                    <span className="absolute -top-2 -right-2" onClick={(e) => { e.preventDefault(); e.stopPropagation(); navigate('/pricing') }}>
                      <ProBadge source="app_detail" feature="resume_match" iconOnly />
                    </span>
                  )}
                </button>
                <button
                  onClick={() => {
                    if (!isPro) {
                      navigate('/pricing')
                      return
                    }
                    navigate(`/resume-builder?tailor=${app.id}`)
                  }}
                  className="relative flex items-center gap-1.5 rounded-lg border border-emerald-300 dark:border-emerald-900/50 bg-emerald-50 dark:bg-emerald-950/30 px-3 py-2 text-sm font-medium text-emerald-700 dark:text-emerald-300 hover:bg-emerald-100 dark:hover:bg-emerald-900/30 transition-colors"
                >
                  <SparklesIcon className="h-4 w-4" />
                  Tailor resume
                  {!isPro && (
                    <span className="absolute -top-2 -right-2" onClick={(e) => { e.preventDefault(); e.stopPropagation(); navigate('/pricing') }}>
                      <ProBadge source="app_detail" feature="resume_builder" iconOnly />
                    </span>
                  )}
                </button>
                {!isTerminalStatus(app.status) && (
                  <button
                    onClick={() => setIsStatusModalOpen(true)}
                    className="flex items-center gap-1.5 rounded-lg border border-app-line px-3 py-2 text-sm font-medium text-app-ink hover:bg-app-subtle dark:bg-app-subtle transition-colors"
                  >
                    <ArrowsRightLeftIcon className="h-4 w-4" />
                    Change status
                  </button>
                )}
                {!isArchived && app.status !== 'rejected' && (
                  <button
                    onClick={() => setIsRejectModalOpen(true)}
                    className="flex items-center gap-1.5 rounded-lg border border-rose-300 dark:border-rose-900/50 bg-rose-100 dark:bg-rose-950/30 px-3 py-2 text-sm font-medium text-rose-900 dark:text-rose-300 hover:bg-rose-200 dark:hover:bg-rose-900/30 transition-colors"
                  >
                    <NoSymbolIcon className="h-4 w-4" />
                    Reject
                  </button>
                )}
                {isArchived ? (
                  <button
                    onClick={() => onUnarchive(app.id)}
                    className="flex items-center gap-1.5 rounded-lg border border-emerald-600 dark:border-emerald-900/50 bg-emerald-100 dark:bg-emerald-950/30 px-3 py-2 text-sm font-medium text-emerald-700 dark:text-emerald-300 hover:bg-emerald-200 dark:hover:bg-emerald-900/30 transition-colors"
                  >
                    Restore
                  </button>
                ) : (
                  <button
                    onClick={() => setIsArchiveModalOpen(true)}
                    className="flex items-center gap-1.5 rounded-lg border border-app-line px-3 py-2 text-sm font-medium text-app-ink hover:bg-app-subtle dark:bg-app-subtle transition-colors"
                  >
                    <ArchiveBoxIcon className="h-4 w-4" />
                    Archive
                  </button>
                )}
              </div>
            </div>

            {/* Row 2: Status badge + source URL */}
            <div className="flex items-center gap-3 flex-wrap">
              <span className={`badge ${statusBadgeClass[app.status]}`}>
                {STATUS_LABELS[app.status]}
              </span>
              {app.sourceUrl && (
                <>
                  <span className="text-app-muted">·</span>
                  <a
                    href={app.sourceUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-1 text-app-muted hover:text-app-accent-hover text-xs transition-colors"
                    title={app.sourceUrl}
                  >
                    <span>{getDomain(app.sourceUrl)}</span>
                    <svg className="h-3 w-3" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 6H5.25A2.25 2.25 0 003 8.25v10.5A2.25 2.25 0 005.25 21h10.5A2.25 2.25 0 0018 18.75V10.5m-10.5 6L21 3m0 0h-5.25M21 3v5.25" />
                    </svg>
                  </a>
                </>
              )}
            </div>

            {/* Offer decision reason */}
            {(app.status === 'offer_declined' || app.status === 'accepted') &&
              app.offerDecisionReason && (
                <div className="text-sm text-app-muted">
                  Reason: {app.offerDecisionReason}
                </div>
              )}
          </div>

          {/* Company Info */}
          {app.companyInfo && (
            <div className="rounded-xl border border-indigo-200 dark:border-indigo-900/50 bg-indigo-50 dark:bg-indigo-950/20 p-4">
              <div className="flex items-start gap-3">
                <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-indigo-200 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-400">
                  <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 21h19.5m-18-18v18m10.5-18v18m6-13.5V21M6.75 6.75h.75m-.75 3h.75m-.75 3h.75m3-6h.75m-.75 3h.75m-.75 3h.75M6.75 21v-3.375c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125V21M3 3h12m-.75 4.5H21m-3.75 3.75h.008v.008h-.008v-.008zm0 3h.008v.008h-.008v-.008zm0 3h.008v.008h-.008v-.008z" />
                  </svg>
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="text-sm font-semibold text-indigo-700 dark:text-indigo-300 mb-1 font-display">Company Info</h3>
                  <p className="text-sm text-app-ink leading-relaxed">{app.companyInfo}</p>
                </div>
              </div>
            </div>
          )}

          {/* Main Content Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Left Column */}
            <div className="space-y-6">
              {/* Position Title */}
              <div className="border border-app-line bg-app-subtle p-4 rounded-2xl shadow-app-card">
                <div className="flex items-center justify-between mb-3">
                  <h2 className="text-lg font-semibold text-app-ink font-display">Position</h2>
                  {!isArchived && !isEditingPosition && (
                    <button
                      onClick={() => setIsEditingPosition(true)}
                      className="p-1.5 text-app-muted hover:bg-app-subtle dark:bg-app-subtle hover:text-app-ink transition-colors rounded-lg"
                      title="Edit position title"
                    >
                      <PencilSquareIcon className="h-4 w-4" />
                    </button>
                  )}
                </div>
                {isEditingPosition ? (
                  <div className="flex items-center gap-2">
                    <input
                      type="text"
                      value={positionTitleDraft}
                      onChange={(e) => setPositionTitleDraft(e.target.value)}
                      placeholder="e.g., Senior Frontend Developer"
                      className="flex-1 rounded-lg border border-app-line bg-app-surface px-3 py-2 text-sm text-app-ink focus:border-app-accent focus:outline-none"
                      autoFocus
                    />
                    <button
                      onClick={() => {
                        setPositionTitleDraft(app.positionTitle ?? '')
                        setIsEditingPosition(false)
                      }}
                      className="rounded-lg border border-app-line px-2 py-1.5 text-xs font-medium text-app-ink hover:bg-app-subtle"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleSavePositionTitle}
                      className="rounded-lg bg-app-primary px-2 py-1.5 text-xs font-medium text-white hover:bg-app-primary-hover"
                    >
                      Save
                    </button>
                  </div>
                ) : (
                  <div>
                    {app.positionTitle ? (
                      <p className="text-base text-app-ink font-medium">{app.positionTitle}</p>
                    ) : (
                      <p className="text-sm text-app-muted italic">No position title</p>
                    )}
                  </div>
                )}
              </div>

              {/* Salary Information */}
              <div className="border border-app-line bg-app-subtle p-4 rounded-2xl shadow-app-card">
                <div className="flex items-center justify-between mb-3">
                  <h2 className="text-lg font-semibold text-app-ink font-display">Compensation</h2>
                  {!isArchived && !isEditingComp && (
                    <button
                      onClick={() => setIsEditingComp(true)}
                      className="p-1.5 text-app-muted hover:bg-app-subtle dark:bg-app-subtle hover:text-app-ink transition-colors rounded-lg"
                      title="Edit"
                    >
                      <PencilSquareIcon className="h-4 w-4" />
                    </button>
                  )}
                </div>

                {isEditingComp ? (
                  <div className="space-y-4">
                    {/* Salary Range */}
                    <div className="space-y-2">
                      <label className="text-xs uppercase text-app-muted font-medium">
                        Salary range (from posting)
                      </label>
                      <div className="grid grid-cols-4 gap-2">
                        <input
                          type="number"
                          min={0}
                          step={1}
                          placeholder="Min"
                          value={salaryMinDraft}
                          onChange={(e) => setSalaryMinDraft(e.target.value)}
                          className="rounded-lg border border-app-line bg-app-surface px-3 py-2 text-sm text-app-ink focus:border-app-accent focus:outline-none"
                        />
                        <input
                          type="number"
                          min={0}
                          step={1}
                          placeholder="Max"
                          value={salaryMaxDraft}
                          onChange={(e) => setSalaryMaxDraft(e.target.value)}
                          className="rounded-lg border border-app-line bg-app-surface px-3 py-2 text-sm text-app-ink focus:border-app-accent focus:outline-none"
                        />
                        <select
                          value={salaryCurrencyDraft}
                          onChange={(e) => setSalaryCurrencyDraft(e.target.value as Currency)}
                          className="rounded-lg border border-app-line bg-app-surface px-2 py-2 text-sm text-app-ink focus:border-app-accent focus:outline-none"
                        >
                          {SUPPORTED_CURRENCIES.map((c) => (
                            <option key={c} value={c}>{c}</option>
                          ))}
                        </select>
                        <select
                          value={salaryPeriodDraft ?? 'monthly'}
                          onChange={(e) => setSalaryPeriodDraft(e.target.value as SalaryPeriod)}
                          className="rounded-lg border border-app-line bg-app-surface px-2 py-2 text-sm text-app-ink focus:border-app-accent focus:outline-none"
                        >
                          <option value="hourly">/hour</option>
                          <option value="monthly">/month</option>
                          <option value="yearly">/year</option>
                        </select>
                      </div>
                    </div>

                    {/* Target Salary */}
                    <div className="space-y-2">
                      <label className="text-xs uppercase text-app-muted font-medium">
                        Your target salary
                      </label>
                      <div className="grid grid-cols-3 gap-2">
                        <input
                          type="number"
                          min={0}
                          step={100}
                          placeholder="Amount"
                          value={targetAmountDraft}
                          onChange={(e) => setTargetAmountDraft(e.target.value)}
                          className="rounded-lg border border-app-line bg-app-surface px-3 py-2 text-sm text-app-ink focus:border-app-accent focus:outline-none"
                        />
                        <select
                          value={targetCurrencyDraft}
                          onChange={(e) => setTargetCurrencyDraft(e.target.value as Currency)}
                          className="rounded-lg border border-app-line bg-app-surface px-2 py-2 text-sm text-app-ink focus:border-app-accent focus:outline-none"
                        >
                          {SUPPORTED_CURRENCIES.map((c) => (
                            <option key={c} value={c}>{c}</option>
                          ))}
                        </select>
                        <select
                          value={targetPeriodDraft ?? 'monthly'}
                          onChange={(e) => setTargetPeriodDraft(e.target.value as SalaryPeriod)}
                          className="rounded-lg border border-app-line bg-app-surface px-2 py-2 text-sm text-app-ink focus:border-app-accent focus:outline-none"
                        >
                          <option value="hourly">/hour</option>
                          <option value="monthly">/month</option>
                          <option value="yearly">/year</option>
                        </select>
                      </div>
                    </div>

                    {compError && <p className="text-sm text-rose-700 dark:text-rose-400">{compError}</p>}

                    <div className="flex justify-end gap-2">
                      <button
                        onClick={() => {
                          setIsEditingComp(false)
                          setCompError(null)
                        }}
                        className="rounded-lg border border-app-line px-3 py-1.5 text-sm font-medium text-app-ink hover:bg-app-subtle dark:bg-app-subtle"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={handleSaveCompensation}
                        disabled={isSavingComp}
                        className="flex items-center gap-2 rounded-lg bg-app-primary px-3 py-1.5 text-sm font-medium text-white hover:bg-app-primary-hover disabled:opacity-50"
                      >
                        {isSavingComp && (
                          <span className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-white border-t-transparent" />
                        )}
                        Save
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-3">
                    <div>
                      <p className="text-xs text-app-muted mb-1">Salary range (posting)</p>
                      <p className="text-sm text-emerald-700 dark:text-emerald-400 font-semibold">{salaryRangeDisplay}</p>
                    </div>
                    <div>
                      <p className="text-xs text-app-muted mb-1">Your target</p>
                      <p className="text-sm text-sky-700 dark:text-sky-300 font-semibold">{targetDisplay}</p>
                    </div>
                  </div>
                )}
              </div>

              {/* Contract Details */}
              <div className="border border-app-line bg-app-subtle p-4 rounded-2xl shadow-app-card">
                <div className="flex items-center justify-between mb-3">
                  <h2 className="text-lg font-semibold text-app-ink font-display">Contract Details</h2>
                  {!isArchived && !isEditingContract && (
                    <button
                      onClick={() => setIsEditingContract(true)}
                      className="p-1.5 text-app-muted hover:bg-app-subtle dark:bg-app-subtle hover:text-app-ink transition-colors rounded-lg"
                      title="Edit"
                    >
                      <PencilSquareIcon className="h-4 w-4" />
                    </button>
                  )}
                </div>

                {isEditingContract ? (
                  <div className="space-y-3">
                    <div>
                      <label className="text-xs text-app-muted mb-1 block">Contract Type</label>
                      <input
                        type="text"
                        value={contractTypeDraft}
                        onChange={(e) => setContractTypeDraft(e.target.value)}
                        placeholder="e.g., B2B, Permanent, Contract"
                        className="w-full rounded-lg border border-app-line bg-app-surface px-3 py-2 text-sm text-app-ink focus:border-app-accent focus:outline-none"
                      />
                    </div>
                    <div>
                      <label className="text-xs text-app-muted mb-1 block">Benefits</label>
                      <input
                        type="text"
                        value={benefitsDraft}
                        onChange={(e) => setBenefitsDraft(e.target.value)}
                        placeholder="e.g., 26 days PTO, Health insurance"
                        className="w-full rounded-lg border border-app-line bg-app-surface px-3 py-2 text-sm text-app-ink focus:border-app-accent focus:outline-none"
                      />
                    </div>
                    <div className="flex justify-end gap-2 pt-2">
                      <button
                        onClick={() => {
                          setContractTypeDraft(app.contractType ?? '')
                          setBenefitsDraft(app.benefits ?? '')
                          setIsEditingContract(false)
                        }}
                        className="rounded-lg border border-app-line px-3 py-1.5 text-sm font-medium text-app-ink hover:bg-app-subtle dark:bg-app-subtle"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={handleSaveContractDetails}
                        className="rounded-lg bg-app-primary px-3 py-1.5 text-sm font-medium text-white hover:bg-app-primary-hover"
                      >
                        Save
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-app-muted">Type:</span>
                      <span className="text-app-ink">{app.contractType || '—'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-app-muted">Benefits:</span>
                      <span className="text-app-ink">{app.benefits || '—'}</span>
                    </div>
                  </div>
                )}
              </div>

              {/* Work Mode */}
              <div className="border border-app-line bg-app-subtle p-4 rounded-2xl shadow-app-card">
                <div className="flex items-center justify-between mb-3">
                  <h2 className="text-lg font-semibold text-app-ink font-display">Work Mode</h2>
                  {!isArchived && !isEditingWorkMode && (
                    <button
                      onClick={() => setIsEditingWorkMode(true)}
                      className="p-1.5 text-app-muted hover:bg-app-subtle dark:bg-app-subtle hover:text-app-ink transition-colors rounded-lg"
                      title="Edit"
                    >
                      <PencilSquareIcon className="h-4 w-4" />
                    </button>
                  )}
                </div>

                {isEditingWorkMode ? (
                  <div className="space-y-3">
                    <div className="grid grid-cols-2 gap-2">
                      <select
                        value={workModeDraft ?? ''}
                        onChange={(e) => {
                          const value = e.target.value as WorkModeType
                          setWorkModeDraft(value || null)
                          if (value !== 'hybrid') setOfficeDaysDraft(null)
                        }}
                        className="w-full rounded-lg border border-app-line bg-app-surface px-3 py-2 text-sm text-app-ink focus:border-app-accent focus:outline-none"
                      >
                        <option value="">Not specified</option>
                        <option value="onsite">On-site</option>
                        <option value="remote">Remote</option>
                        <option value="hybrid">Hybrid</option>
                      </select>
                      {workModeDraft === 'hybrid' && (
                        <select
                          value={officeDaysDraft ?? ''}
                          onChange={(e) => setOfficeDaysDraft(e.target.value ? Number(e.target.value) : null)}
                          className="w-full rounded-lg border border-app-line bg-app-surface px-3 py-2 text-sm text-app-ink focus:border-app-accent focus:outline-none"
                        >
                          <option value="">Days in office</option>
                          <option value="1">1 day in office</option>
                          <option value="2">2 days in office</option>
                          <option value="3">3 days in office</option>
                          <option value="4">4 days in office</option>
                          <option value="5">5 days in office</option>
                        </select>
                      )}
                    </div>
                    <div className="flex justify-end gap-2 pt-2">
                      <button
                        onClick={() => {
                          setWorkModeDraft(app.workMode ?? null)
                          setOfficeDaysDraft(app.officeDays ?? null)
                          setIsEditingWorkMode(false)
                        }}
                        className="rounded-lg border border-app-line px-3 py-1.5 text-sm font-medium text-app-ink hover:bg-app-subtle dark:bg-app-subtle"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={handleSaveWorkMode}
                        className="rounded-lg bg-app-primary px-3 py-1.5 text-sm font-medium text-white hover:bg-app-primary-hover"
                      >
                        Save
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="text-sm">
                    {app.workMode ? (
                      <span className="text-app-ink">
                        {app.workMode === 'onsite' && 'On-site'}
                        {app.workMode === 'remote' && 'Remote'}
                        {app.workMode === 'hybrid' && (
                          <>Hybrid{app.officeDays && ` (${app.officeDays} days in office)`}</>
                        )}
                      </span>
                    ) : (
                      <span className="text-app-muted">Not specified</span>
                    )}
                  </div>
                )}
              </div>

              {/* Tech Stack */}
              <div className="border border-app-line bg-app-subtle p-4 rounded-2xl shadow-app-card">
                <div className="flex items-center justify-between mb-3">
                  <h2 className="text-lg font-semibold text-app-ink font-display">Tech Stack</h2>
                  {!isArchived && !isEditingTechStack && (
                    <button
                      onClick={() => setIsEditingTechStack(true)}
                      className="p-1.5 text-app-muted hover:bg-app-subtle dark:bg-app-subtle hover:text-app-ink transition-colors rounded-lg"
                      title="Edit"
                    >
                      <PencilSquareIcon className="h-4 w-4" />
                    </button>
                  )}
                </div>

                {isEditingTechStack ? (
                  <div className="space-y-3">
                    <div>
                      <input
                        type="text"
                        value={techStackDraft}
                        onChange={(e) => setTechStackDraft(e.target.value)}
                        placeholder="React, TypeScript, Node.js"
                        className="w-full rounded-lg border border-app-line bg-app-surface px-3 py-2 text-sm text-app-ink focus:border-app-accent focus:outline-none"
                      />
                      <p className="text-xs text-app-muted mt-1">Separate with commas</p>
                    </div>
                    <div className="flex justify-end gap-2">
                      <button
                        onClick={() => {
                          setTechStackDraft(app.techStack.join(', '))
                          setIsEditingTechStack(false)
                        }}
                        className="rounded-lg border border-app-line px-3 py-1.5 text-sm font-medium text-app-ink hover:bg-app-subtle dark:bg-app-subtle"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={handleSaveTechStack}
                        className="rounded-lg bg-app-primary px-3 py-1.5 text-sm font-medium text-white hover:bg-app-primary-hover"
                      >
                        Save
                      </button>
                    </div>
                  </div>
                ) : app.techStack.length > 0 ? (
                  <div className="flex flex-wrap gap-2">
                    {app.techStack.map((tech) => (
                      <span
                        key={tech}
                        className="px-2.5 py-1 rounded-full bg-app-page border border-app-line text-app-ink text-xs font-medium"
                      >
                        {tech}
                      </span>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-app-muted">No tech stack specified</p>
                )}
              </div>

            </div>

            {/* Right Column */}
            <div className="space-y-6">
              {/* Stages */}
              <div className="border border-app-line bg-app-subtle p-4 rounded-2xl shadow-app-card">
                <div className="flex items-center justify-between mb-3">
                  <h2 className="text-lg font-semibold text-app-ink font-display">Process Stages</h2>
                  {!isArchived && !isEditingStages && (
                    <button
                      onClick={() => setIsEditingStages(true)}
                      className="p-1.5 text-app-muted hover:bg-app-subtle dark:bg-app-subtle hover:text-app-ink transition-colors rounded-lg"
                      title="Edit stages"
                    >
                      <PencilSquareIcon className="h-4 w-4" />
                    </button>
                  )}
                </div>

                {isEditingStages ? (
                  <div className="space-y-3">
                    {stageDrafts.length === 0 ? (
                      <p className="text-sm text-app-muted text-center py-4">
                        No stages yet. Add your first stage below.
                      </p>
                    ) : (
                      <div className="space-y-2">
                        {stageDrafts.map((stage, idx) => (
                          <div
                            key={stage.id}
                            draggable={!isSavingStages}
                            onDragStart={(e) => handleDragStart(idx, e)}
                            onDragOver={(e) => handleDragOver(idx, e)}
                            onDrop={(e) => handleDrop(idx, e)}
                            onDragEnd={handleDragEnd}
                            className={`flex items-center gap-2 rounded-lg border px-2 py-1.5 ${
                              draggedIndex === idx
                                ? 'border-sky-600 bg-app-page'
                                : 'border-app-line bg-app-page'
                            }`}
                          >
                            <Bars3Icon className="h-4 w-4 text-app-muted cursor-grab shrink-0" />
                            <select
                              value={stage.name}
                              onChange={(e) => updateStageDraft(stage.id, { name: e.target.value })}
                              className="flex-1 min-w-0 border border-app-line bg-app-page px-2 py-1 text-xs text-app-ink focus:border-app-accent focus:outline-none rounded-lg"
                            >
                              {PRESET_STAGE_OPTIONS.map((opt) => (
                                <option key={opt} value={opt}>
                                  {opt}
                                </option>
                              ))}
                              {!PRESET_STAGE_OPTIONS.includes(stage.name) && (
                                <option value={stage.name}>{stage.name}</option>
                              )}
                            </select>
                            <button
                              type="button"
                              onClick={() => updateStageDraft(stage.id, { completed: !stage.completed })}
                              className={`shrink-0 rounded-full px-2 py-0.5 text-xs font-medium transition-colors ${
                                stage.completed
                                  ? 'bg-emerald-50 dark:bg-emerald-900/50 text-emerald-700 dark:text-emerald-300 hover:bg-emerald-50 dark:hover:bg-emerald-900/70'
                                  : 'bg-app-surface text-app-muted hover:bg-app-surface'
                              }`}
                            >
                              {stage.completed ? '✓ Done' : 'Todo'}
                            </button>
                            <button
                              onClick={() => removeStageDraft(stage.id)}
                              className="shrink-0 p-1 text-app-muted hover:bg-app-surface hover:text-rose-700 rounded-lg"
                            >
                              <XMarkIcon className="h-4 w-4" />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}

                    <button
                      onClick={addStageDraft}
                      className="w-full rounded-lg border border-dashed border-app-line px-3 py-2 text-sm text-app-muted hover:border-app-line hover:text-app-ink hover:bg-app-subtle dark:bg-app-subtle/30 transition-colors"
                    >
                      + Add stage
                    </button>

                    <div className="flex justify-end gap-2 pt-2">
                      <button
                        onClick={() => {
                          setStageDrafts(app.processStages)
                          setIsEditingStages(false)
                        }}
                        className="rounded-lg border border-app-line px-3 py-1.5 text-sm font-medium text-app-ink hover:bg-app-subtle dark:bg-app-subtle"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={saveStages}
                        disabled={isSavingStages}
                        className="flex items-center gap-2 rounded-lg bg-app-primary px-3 py-1.5 text-sm font-medium text-white hover:bg-app-primary-hover disabled:opacity-50"
                      >
                        {isSavingStages && (
                          <span className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-white border-t-transparent" />
                        )}
                        Save stages
                      </button>
                    </div>
                  </div>
                ) : app.processStages.length > 0 ? (
                  <div className="space-y-2">
                    {app.processStages.map((stage, idx) => {
                      const disabled = isArchived || isTerminalStatus(app.status)
                      return (
                        <button
                          key={stage.id}
                          type="button"
                          disabled={disabled}
                          onClick={() => onToggleStage(app.id, stage.id)}
                          className={`flex w-full items-center gap-3 rounded-lg border px-3 py-2 transition-colors ${
                            stage.completed
                              ? 'border-sky-500/60 bg-sky-100 dark:bg-sky-950/30'
                              : 'border-app-line bg-app-page'
                          } ${disabled ? 'opacity-60 cursor-not-allowed' : 'hover:border-sky-700'}`}
                        >
                          <div
                            className={`w-6 h-6 rounded-full border-2 flex items-center justify-center text-xs font-bold shrink-0 ${
                              stage.completed
                                ? 'bg-sky-600 border-sky-500 text-white'
                                : 'border-app-line text-app-muted'
                            }`}
                          >
                            {stage.completed ? <CheckIcon className="h-3.5 w-3.5" /> : idx + 1}
                          </div>
                          <span
                            className={`text-sm ${
                              stage.completed ? 'text-app-muted' : 'text-app-ink'
                            }`}
                          >
                            {stage.name}
                          </span>
                        </button>
                      )
                    })}
                    {!isArchived && !isTerminalStatus(app.status) && (
                      <p className="text-xs text-app-muted mt-2">
                        Click a stage to toggle completion
                      </p>
                    )}
                  </div>
                ) : (
                  <p className="text-sm text-app-muted">No stages defined yet.</p>
                )}
              </div>

              {/* Note */}
              <div className="border border-app-line bg-app-subtle p-4 rounded-2xl shadow-app-card">
                <div className="flex items-center justify-between mb-3">
                  <h2 className="text-lg font-semibold text-app-ink font-display">Note</h2>
                  {!isArchived && !isEditingNote && (
                    <button
                      onClick={() => {
                        setNoteDraft(app.note ?? '')
                        setIsEditingNote(true)
                        setTimeout(() => noteTextareaRef.current?.focus(), 0)
                      }}
                      className="p-1.5 text-app-muted hover:bg-app-subtle dark:bg-app-subtle hover:text-app-ink transition-colors rounded-lg"
                      title="Edit note"
                    >
                      <PencilSquareIcon className="h-4 w-4" />
                    </button>
                  )}
                </div>

                {isEditingNote ? (
                  <div className="space-y-3">
                    <textarea
                      ref={noteTextareaRef}
                      rows={4}
                      value={noteDraft}
                      onChange={(e) => setNoteDraft(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Escape') {
                          setNoteDraft(app.note ?? '')
                          setIsEditingNote(false)
                        }
                        if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
                          onUpdateNote(app.id, noteDraft)
                          setIsEditingNote(false)
                        }
                      }}
                      className="w-full rounded-lg border border-app-line bg-app-surface px-3 py-2 text-sm text-app-ink focus:border-app-accent focus:outline-none resize-none"
                      placeholder="Add a note about this application..."
                    />
                    <div className="flex items-center justify-between">
                      <p className="text-xs text-app-muted">Ctrl+Enter to save, Esc to cancel</p>
                      <div className="flex gap-2">
                        <button
                          onClick={() => {
                            setNoteDraft(app.note ?? '')
                            setIsEditingNote(false)
                          }}
                          className="rounded-lg border border-app-line px-3 py-1.5 text-sm font-medium text-app-ink hover:bg-app-subtle dark:bg-app-subtle"
                        >
                          Cancel
                        </button>
                        <button
                          onClick={() => {
                            onUpdateNote(app.id, noteDraft)
                            setIsEditingNote(false)
                          }}
                          className="rounded-lg bg-app-primary px-3 py-1.5 text-sm font-medium text-white hover:bg-app-primary-hover"
                        >
                          Save
                        </button>
                      </div>
                    </div>
                  </div>
                ) : app.note ? (
                  <p className="text-sm text-app-ink whitespace-pre-wrap">{app.note}</p>
                ) : (
                  <p className="text-sm text-app-muted italic">No note added yet.</p>
                )}
              </div>
            </div>
          </div>

          {/* Raw Markdown (if available) - Pro only */}
          {app.rawMarkdown && (
            <div className="border-t border-app-line pt-6">
              {isPro ? (
                <>
                  <button
                    type="button"
                    onClick={() => setIsJobContentExpanded(!isJobContentExpanded)}
                    className="flex w-full items-center justify-between text-lg font-semibold text-app-ink mb-3 hover:text-app-accent-hover transition-colors"
                  >
                    <span>Job Posting Content</span>
                    <ChevronDownIcon
                      className={`h-5 w-5 transition-transform duration-200 ${isJobContentExpanded ? 'rotate-180' : ''}`}
                    />
                  </button>
                  {isJobContentExpanded && (
                    <div className="max-h-96 overflow-y-auto bg-app-surface border border-app-line p-4 rounded-2xl shadow-app-card">
                      <div className="prose prose-invert prose-sm max-w-none text-app-ink text-xs whitespace-pre-wrap break-words">
                        {app.rawMarkdown.substring(0, 3000)}
                        {app.rawMarkdown.length > 3000 && (
                          <p className="text-app-muted mt-2">[Content truncated...]</p>
                        )}
                      </div>
                    </div>
                  )}
                </>
              ) : (
                <div className="border border-app-line bg-app-subtle p-4 rounded-2xl shadow-app-card">
                  <div className="flex items-center gap-3 text-app-muted">
                    <LockClosedIcon className="h-5 w-5" />
                    <div>
                      <p className="font-medium text-app-ink">Job Posting Content</p>
                      <p className="text-sm">Upgrade to Pro to view the original job posting content.</p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Metadata */}
          <div className="border-t border-app-line pt-6 text-xs text-app-muted flex flex-wrap gap-x-6 gap-y-1">
            <span>Created: {formatDate(app.createdAt, settings.dateFormat)}</span>
            <span>Updated: {formatDate(app.updatedAt, settings.dateFormat)}</span>
            <span>ID: {app.id}</span>
          </div>
        </div>
      </div>

      {/* Status Change Modal */}
      {isStatusModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 px-4">
          <div
            ref={statusModalRef}
            className="w-full max-w-lg border border-app-line bg-app-surface p-4 space-y-4 rounded-2xl shadow-app-card"
          >
            <div className="flex items-center justify-between">
              <p className="text-base font-semibold text-app-ink flex items-center gap-2">
                <ArrowsRightLeftIcon className="h-5 w-5" /> Change Status
              </p>
              <button
                onClick={() => setIsStatusModalOpen(false)}
                className="p-1 text-app-muted hover:bg-app-subtle dark:bg-app-subtle hover:text-app-ink rounded-lg"
              >
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>

            <div className="space-y-3">
              <div>
                <label className="text-xs uppercase text-app-muted mb-1 block">New status</label>
                <select
                  value={statusDraft}
                  onChange={(e) => setStatusDraft(e.target.value as ApplicationStatus)}
                  className="w-full rounded-lg border border-app-line bg-app-surface px-3 py-2 text-sm text-app-ink focus:border-app-accent focus:outline-none"
                >
                  <option value={app.status}>{STATUS_LABELS[app.status]} (current)</option>
                  {getValidTransitions(app.status).map((status) => (
                    <option key={status} value={status}>
                      {STATUS_LABELS[status]}
                    </option>
                  ))}
                </select>
              </div>

              {(statusDraft === 'offer_declined' || statusDraft === 'accepted') && (
                <div>
                  <label className="text-xs uppercase text-app-muted mb-1 block">
                    Decision reason
                  </label>
                  <input
                    type="text"
                    value={offerReasonDraft}
                    onChange={(e) => setOfferReasonDraft(e.target.value)}
                    placeholder="e.g. better offer elsewhere"
                    className="w-full rounded-lg border border-app-line bg-app-surface px-3 py-2 text-sm text-app-ink focus:border-app-accent focus:outline-none"
                  />
                </div>
              )}
            </div>

            <div className="flex justify-end gap-2 pt-2">
              <button
                onClick={() => {
                  setStatusDraft(app.status)
                  setOfferReasonDraft(app.offerDecisionReason ?? '')
                  setIsStatusModalOpen(false)
                }}
                className="rounded-lg border border-app-line px-4 py-2 text-sm font-medium text-app-ink hover:bg-app-subtle dark:bg-app-subtle"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  onUpdateStatus(app.id, statusDraft, offerReasonDraft || null)
                  setIsStatusModalOpen(false)
                }}
                disabled={statusDraft === app.status}
                className="flex items-center gap-2 rounded-lg bg-sky-600 px-4 py-2 text-sm font-medium text-app-ink hover:bg-sky-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <CheckIcon className="h-4 w-4" />
                Save
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reject Modal */}
      {isRejectModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 px-4">
          <div className="w-full max-w-lg border border-app-line bg-app-surface p-4 space-y-4 rounded-2xl shadow-app-card">
            <div className="flex items-center justify-between">
              <p className="text-base font-semibold text-rose-700 dark:text-rose-300 flex items-center gap-2">
                <NoSymbolIcon className="h-5 w-5" /> Reject Application
              </p>
              <button
                onClick={() => setIsRejectModalOpen(false)}
                className="p-1 text-app-muted hover:bg-app-subtle dark:bg-app-subtle hover:text-app-ink rounded-lg"
              >
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>

            <div className="space-y-3">
              <div>
                <label className="text-xs uppercase text-app-muted mb-1 block">Reason</label>
                <select
                  value={rejectState.code}
                  onChange={(e) =>
                    setRejectState({ ...rejectState, code: e.target.value as RejectReasonCode })
                  }
                  className="w-full rounded-lg border border-app-line bg-app-surface px-3 py-2 text-sm text-app-ink focus:border-app-accent focus:outline-none"
                >
                  {rejectOptions.map((opt) => (
                    <option key={opt.code} value={opt.code}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-xs uppercase text-app-muted mb-1 block">
                  Details (optional)
                </label>
                <input
                  type="text"
                  value={rejectState.details ?? ''}
                  onChange={(e) => setRejectState({ ...rejectState, details: e.target.value })}
                  placeholder="Additional context..."
                  className="w-full rounded-lg border border-app-line bg-app-surface px-3 py-2 text-sm text-app-ink focus:border-app-accent focus:outline-none"
                />
              </div>
            </div>

            <div className="flex justify-end gap-2 pt-2">
              <button
                onClick={() => setIsRejectModalOpen(false)}
                className="rounded-lg border border-app-line px-4 py-2 text-sm font-medium text-app-ink hover:bg-app-subtle dark:bg-app-subtle"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  onSubmitReject(app.id, rejectState)
                  setIsRejectModalOpen(false)
                }}
                className="rounded-lg bg-rose-600 px-4 py-2 text-sm font-medium text-app-ink hover:bg-rose-700"
              >
                Reject
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Archive Confirmation Modal */}
      {isArchiveModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 px-4">
          <div className="w-full max-w-md border border-app-line bg-app-surface p-4 space-y-4 rounded-2xl shadow-app-card">
            <div className="flex items-center justify-between">
              <p className="text-base font-semibold text-app-ink flex items-center gap-2">
                <ArchiveBoxIcon className="h-5 w-5" /> Archive Application
              </p>
              <button
                onClick={() => setIsArchiveModalOpen(false)}
                className="p-1 text-app-muted hover:bg-app-subtle dark:bg-app-subtle hover:text-app-ink rounded-lg"
              >
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>

            <p className="text-sm text-app-ink">
              Are you sure you want to archive this application? You can restore it later from the archived section.
            </p>

            <div className="flex justify-end gap-2 pt-2">
              <button
                onClick={() => setIsArchiveModalOpen(false)}
                className="rounded-lg border border-app-line px-4 py-2 text-sm font-medium text-app-ink hover:bg-app-subtle dark:bg-app-subtle"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  onArchive(app.id)
                  setIsArchiveModalOpen(false)
                }}
                className="rounded-lg bg-app-surface px-4 py-2 text-sm font-medium text-app-ink hover:bg-app-surface"
              >
                Archive
              </button>
            </div>
          </div>
        </div>
      )}

      {/* No Resume Modal */}
      {isNoResumeModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 px-4">
          <div className="w-full max-w-md border border-app-line bg-app-surface p-4 space-y-4 rounded-2xl shadow-app-card">
            <div className="flex items-center justify-between">
              <p className="text-base font-semibold text-app-ink flex items-center gap-2">
                <DocumentTextIcon className="h-5 w-5 text-sky-500" /> Resume Required
              </p>
              <button
                onClick={() => setIsNoResumeModalOpen(false)}
                className="p-1 text-app-muted hover:bg-app-subtle rounded-lg"
              >
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>

            <p className="text-sm text-app-ink">
              To compare your resume with this job application, you need to upload and analyze your resume first.
            </p>

            <div className="flex justify-end gap-2 pt-2">
              <button
                onClick={() => setIsNoResumeModalOpen(false)}
                className="rounded-lg border border-app-line px-4 py-2 text-sm font-medium text-app-ink hover:bg-app-subtle"
              >
                Cancel
              </button>
              <Link
                to="/resume"
                onClick={() => setIsNoResumeModalOpen(false)}
                className="rounded-lg bg-app-primary px-4 py-2 text-sm font-medium text-white hover:bg-app-primary-hover"
              >
                Go to Resume Analysis
              </Link>
            </div>
          </div>
        </div>
      )}
    </>
  )
}




